import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faChevronLeft,
  faClock,
  faEnvelope,
  faMapMarkerAlt,
  faPaperPlane,
  faPhone,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Žádný backend pro form-submit zatím nemáme — proto formulář otevírá
 * mailto: s předvyplněným tělem. Až bude /api/site/contact nebo podobný
 * endpoint, stačí přepsat handleSubmit.
 */
const ContactPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    salon: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Reservare — dotaz od ${form.name || form.email}`);
    const body = encodeURIComponent(
      `Salon: ${form.salon || '(nevyplněno)'}\n` +
      `Jméno: ${form.name}\n` +
      `E-mail: ${form.email}\n\n` +
      `${form.message}`
    );
    window.location.href = `mailto:info@reservare.cz?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/site" className="flex items-center gap-3 text-gray-900 hover:text-primary-600 transition-colors">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg flex items-center justify-center text-white">
              <FontAwesomeIcon icon={faCalendarCheck} className="text-lg" />
            </div>
            <span className="text-xl font-bold">Reservare</span>
          </Link>
          <Link to="/site" className="text-sm text-gray-600 hover:text-primary-600 transition-colors">
            <FontAwesomeIcon icon={faChevronLeft} className="mr-1" /> Zpět
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Kontakt</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Pište, voláme zpátky nebo napíšeme. Pilotní nasazení do hodiny od domluvy.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <ContactTile icon={faEnvelope} label="E-mail" value="info@reservare.cz" href="mailto:info@reservare.cz" />
          <ContactTile icon={faPhone} label="Telefon" value="+420 ..." href="#" hint="Po e-mailu domluvíme hovor" />
          <ContactTile icon={faClock} label="Odezva" value="do 1 prac. dne" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Napište nám</h2>
          <p className="text-sm text-gray-600 mb-6">
            Formulář otevře váš e-mailový klient s předvyplněnou zprávou na <code className="font-mono text-xs">info@reservare.cz</code>.
          </p>
          <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Jméno *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Salon / firma</label>
              <input
                name="salon"
                value={form.salon}
                onChange={handleChange}
                placeholder="Název salonu nebo firmy"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Zpráva *</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
                required
                placeholder="Kolik máte zaměstnanců, jestli chcete WhatsApp + AI, jak rezervujete teď…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                Odeslat
              </button>
            </div>
          </form>
        </div>

        <div className="mt-10 text-center text-sm text-gray-600">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-gray-400" />
          Reservare běží na vlastní infrastruktuře v EU. Hosting i data v Hetzner Cloud (Helsinky / Falkenstein).
        </div>
      </main>
    </div>
  );
};

interface ContactTileProps {
  icon: typeof faEnvelope;
  label: string;
  value: string;
  href?: string;
  hint?: string;
}

const ContactTile = ({ icon, label, value, href, hint }: ContactTileProps) => {
  const content = (
    <>
      <div className="w-12 h-12 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center mb-3">
        <FontAwesomeIcon icon={icon} className="text-xl" />
      </div>
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
      <p className="text-lg font-semibold text-gray-900">{value}</p>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </>
  );
  return href ? (
    <a href={href} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow block">
      {content}
    </a>
  ) : (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">{content}</div>
  );
};

export default ContactPage;
